/**
 * FileUpload Component
 *
 * Reusable file upload with drag & drop, preview, and size validation.
 * Supports single (default) and multiple modes :
 *  - single   : props existantes (`onFileSelect`, preview interne)
 *  - multiple : `multiple={true}` + `onFilesSelect` ; ne rend que la dropzone
 *               (le caller affiche sa propre liste pour gerer noms / status).
 *
 * @see US-36.7 - Upload BL fournisseur (single)
 * @see Sprint BDisplay multi-upload (multi)
 */

import { useCallback, useRef, useState } from 'react';
import { Upload, FileText, Image, X, AlertCircle } from 'lucide-react';

export interface FileUploadProps {
  /** Label affiché au-dessus du composant */
  label: string;
  /** Types de fichiers acceptés (ex: ".pdf,.jpg,.jpeg,.png") */
  accept?: string;
  /** Taille max en MB par fichier (défaut: 10) */
  maxSizeMB?: number;
  /** Callback quand un fichier est sélectionné (mode single uniquement) */
  onFileSelect?: (file: File) => void;
  /** Mode single : prevenu quand l'utilisateur retire le fichier choisi (croix de l'apercu). */
  onClear?: () => void;
  /**
   * Mode multi : si `true`, accepte plusieurs fichiers, n'affiche pas le
   * preview interne (le caller gère sa liste) et active l'attribut
   * `multiple` sur l'input. Le drag-and-drop accepte plusieurs fichiers.
   */
  multiple?: boolean;
  /**
   * Callback en mode multi. Reçoit la liste des fichiers valides (taille +
   * type OK) après chaque drop / selection. Les fichiers invalides sont
   * filtrés silencieusement (un message d'erreur agrégé est rendu sous la
   * dropzone).
   */
  onFilesSelect?: (files: File[]) => void;
  /**
   * Nombre max de fichiers en une seule sélection / drop (mode multi).
   * Si dépassé, on garde les `maxFiles` premiers et on affiche un warning.
   * Defaut : 10.
   */
  maxFiles?: number;
  /**
   * Callback optionnel appele en mode multi quand certains fichiers ont
   * ete rejetes (taille, type, ou depassement de `maxFiles`). Permet au
   * caller d'afficher un toast bien visible — sinon l'erreur reste
   * affichee inline sous la dropzone (fallback).
   */
  onValidationError?: (errors: string[]) => void;
  /** Désactiver le composant */
  disabled?: boolean;
  /** Fichier en cours d'upload (mode single — pour afficher un loader) */
  uploading?: boolean;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(file: File) {
  if (file.type === 'application/pdf') {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  if (file.type.startsWith('image/')) {
    return <Image className="w-5 h-5 text-blue-500" />;
  }
  return <FileText className="w-5 h-5 text-neutral-500" />;
}

/**
 * Valide un fichier (taille + type) et retourne une erreur ou null.
 * Centralise la logique pour le mode single et multi.
 */
function validateFile(
  file: File,
  accept: string | undefined,
  maxSizeBytes: number,
  maxSizeMB: number
): string | null {
  if (file.size > maxSizeBytes) {
    return `Fichier trop volumineux (max ${maxSizeMB} MB)`;
  }
  if (accept) {
    const allowedExts = accept.split(',').map((ext) => ext.trim().toLowerCase());
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    const allowedMimes = allowedExts
      .map((ext): string | null => {
        if (ext === '.pdf') return 'application/pdf';
        if (ext === '.jpg' || ext === '.jpeg') return 'image/jpeg';
        if (ext === '.png') return 'image/png';
        if (ext === '.webp') return 'image/webp';
        if (ext === '.gif') return 'image/gif';
        if (ext === '.mp4') return 'video/mp4';
        if (ext === '.mov') return 'video/quicktime';
        if (ext === '.webm') return 'video/webm';
        if (ext === '.mkv') return 'video/x-matroska';
        return null;
      })
      .filter((m): m is string => m !== null);
    if (!allowedExts.includes(fileExt) && !allowedMimes.includes(file.type)) {
      return `Type de fichier non accepte (${accept})`;
    }
  }
  return null;
}

export function FileUpload({
  label,
  accept = '.pdf,.jpg,.jpeg,.png',
  maxSizeMB = 10,
  onFileSelect,
  onClear,
  multiple = false,
  onFilesSelect,
  maxFiles = 10,
  onValidationError,
  disabled = false,
  uploading = false,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // ---- Mode single ---------------------------------------------------------
  const processFileSingle = useCallback(
    (file: File) => {
      setError(null);
      const err = validateFile(file, accept, maxSizeBytes, maxSizeMB);
      if (err) {
        setError(err);
        return;
      }
      setSelectedFile(file);
      onFileSelect?.(file);
    },
    [accept, maxSizeBytes, maxSizeMB, onFileSelect]
  );

  // ---- Mode multi ----------------------------------------------------------
  const processFilesMulti = useCallback(
    (rawFiles: FileList | File[]) => {
      setError(null);
      const list = Array.from(rawFiles);
      // On limite avant validation pour eviter de rejeter en silence des
      // fichiers qui auraient passe la limite mais sont au-dela du `maxFiles`.
      const overLimit = list.length > maxFiles;
      const sliced = list.slice(0, maxFiles);
      const valid: File[] = [];
      const errors: string[] = [];
      for (const file of sliced) {
        const err = validateFile(file, accept, maxSizeBytes, maxSizeMB);
        if (err) {
          errors.push(`${file.name} : ${err}`);
        } else {
          valid.push(file);
        }
      }
      if (overLimit) {
        errors.unshift(
          `Maximum ${maxFiles} fichiers par lot — les ${list.length - maxFiles} suivants sont ignores`
        );
      }
      if (errors.length > 0) {
        // Si le caller fournit un handler, on lui delegue (typiquement un toast
        // bien visible). Sinon on affiche l'erreur inline (fallback).
        if (onValidationError) {
          onValidationError(errors);
        } else {
          setError(errors.join(' · '));
        }
      }
      if (valid.length > 0) {
        onFilesSelect?.(valid);
      }
    },
    [accept, maxSizeBytes, maxSizeMB, maxFiles, onFilesSelect, onValidationError]
  );

  // ---- Handlers DOM --------------------------------------------------------
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setDragOver(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;
      if (multiple) {
        processFilesMulti(files);
      } else {
        const file = files[0];
        if (file) processFileSingle(file);
      }
    },
    [disabled, multiple, processFilesMulti, processFileSingle]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        if (multiple) {
          processFilesMulti(files);
        } else {
          const file = files[0];
          if (file) processFileSingle(file);
        }
      }
      // Reset input so the same file can be re-selected
      if (inputRef.current) inputRef.current.value = '';
    },
    [multiple, processFilesMulti, processFileSingle]
  );

  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    onClear?.();
  }, [onClear]);

  // En mode multi, le caller affiche sa propre liste — on n'utilise pas
  // `selectedFile` du tout. La dropzone reste toujours visible et on accumule
  // les drops via le callback.
  const showSinglePreview = !multiple && selectedFile && !error;

  return (
    <div>
      <label className="block text-sm font-medium text-dark-500 mb-1.5">{label}</label>

      {showSinglePreview ? (
        /* File selected — show preview (mode single uniquement) */
        <div className="flex items-center gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
          {getFileIcon(selectedFile)}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-dark-500 truncate">{selectedFile.name}</p>
            <p className="text-xs text-neutral-500">{formatSize(selectedFile.size)}</p>
          </div>
          {uploading ? (
            <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        /* Drop zone */
        <button
          type="button"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className={`
            w-full flex flex-col items-center justify-center gap-2 p-4
            border-2 border-dashed rounded-lg cursor-pointer transition-colors
            ${disabled ? 'opacity-50 cursor-not-allowed bg-neutral-50 border-neutral-200' : ''}
            ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-neutral-300 hover:border-primary-300 hover:bg-neutral-50'}
          `}
        >
          <Upload className={`w-6 h-6 ${dragOver ? 'text-primary-500' : 'text-neutral-400'}`} />
          <div className="text-center">
            <p className="text-sm text-neutral-600">
              <span className="font-medium text-primary-600">Parcourir</span>{' '}
              ou glisser-deposer
              {multiple ? <> (jusqu'a {maxFiles} fichiers)</> : null}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">
              {accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')} - Max {maxSizeMB} MB
              {multiple ? ' / fichier' : ''}
            </p>
          </div>
        </button>
      )}

      {error && (
        <div className="flex items-start gap-1.5 mt-1.5 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
}
